import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import Media from 'react-media';
import loadable from '@loadable/component';

import { Modal, useTheme } from '@simplywallst/ui-core';
import { Button } from '@simplywallst/ui-core/next';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';
import { push } from 'redux-first-router';
import { useTracking } from 'react-tracking';

const AlgoliaSearch = loadable(
  () => import('@/components/Search/components/AlgoliaSearch')
);
const BasicSearch = loadable(
  () => import('@/components/Search/components/BasicSearch'),
  {
    fallback: <div className="bones h-9 w-[146px]" />,
  }
);

type Props = {
  companyName: string | undefined | null;
};

export const NarrativeCommunityCompanySearch = ({ companyName }: Props) => {
  const theme = useTheme();
  const [showSearch, setShowSearch] = useState(false);

  const { trackEvent } = useTracking();

  const handleClick = useCallback(() => {
    trackEvent({
      action: 'click',
      subject: 'search_company_button',
    });
    setShowSearch((prevState) => !prevState);
  }, [trackEvent]);

  const handleSelect = useCallback(
    async ({ canonicalUrl }: { canonicalUrl: string }) => {
      const communityCanonicalUrl = canonicalUrl.replace(
        '/stocks',
        '/community/narratives'
      );

      push(communityCanonicalUrl);

      setShowSearch(false);
    },
    []
  );

  const getResult = useCallback(
    (
      child: ReactNode,
      result: {
        id?: string;
        uniqueSymbol: string;
        canonicalUrl: string;
      }
    ) => (
      <div
        className="cursor-pointer"
        onClick={() => {
          handleSelect({
            canonicalUrl: result.canonicalUrl,
          });
        }}
      >
        {child}
      </div>
    ),
    [handleSelect]
  );

  return (
    <section className="grid min-h-9 items-center">
      {showSearch ? (
        <Media query={{ maxWidth: theme.mdMaxRaw }}>
          {(match) => {
            if (match) {
              return (
                <Modal isNaked onSecondaryClick={handleClick}>
                  <AlgoliaSearch
                    placeholder="Select a company"
                    onClose={handleClick}
                    primaryWrapper={getResult}
                    secondaryWrapper={getResult}
                  />
                </Modal>
              );
            }
            return (
              <BasicSearch
                size="medium"
                placeholder="Select a company"
                onClick={(_uniqueSymbol, { canonicalUrl }) =>
                  handleSelect({ canonicalUrl })
                }
              />
            );
          }}
        </Media>
      ) : (
        <Button
          variant="outline"
          onClick={handleClick}
          className="inline-grid grid-cols-[auto_max-content] items-center justify-between gap-x1"
          size="medium"
        >
          <span className="truncate">{companyName || 'All companies'}</span>
          <ChevronDownSmallSolid />
        </Button>
      )}
    </section>
  );
};
